import * as THREE from 'three';

import { EDirection, EWall, EWindingOrder } from '../@types/Enums';
import { DEFAULT_WALL_OVERLAY_DISTANCE, HALF_CIRCLE_DEGREES, POLYGON_BOUNDARY_TOLERANCE, POLYGON_CENTROID_MULTIPLIER, ROOM_COMPLETION_TOLERANCE } from './constants';

import { Vector3 } from 'three';
import { IWall } from '../models/abstract/Wall';
import { IPartitionWall } from '../models/standard/PartitionWallModel';
import StandardWallModel, { IStandardWall } from '../models/standard/StandardWallModel';
import { hasIntersections } from './validationUtils';

/**
 * @param {THREE.Vector3} point
 * @param {THREE.Vector3} startPoint
 * @param {THREE.Vector3} endPoint
 * @return {*}  {number}
 */
export const getProjectionDistance = (point: THREE.Vector3, startPoint: THREE.Vector3, endPoint: THREE.Vector3): number => {
  const lineVector = new THREE.Vector3().subVectors(endPoint, startPoint);
  const normalizedLine = lineVector.clone().normalize();
  const pointVector = new THREE.Vector3().subVectors(point, startPoint);
  return pointVector.dot(normalizedLine);
};

/**
 *  Calculate the winding order of a set of points.
 * The function uses the shoelace formula to calculate the signed area.
 * The sign of the area can be used to determine the winding order.
 *
 * @param {Array} wallModels - An array of points where each point is represented as [x, y].
 * @returns {Symbol} - WindingOrder.CLOCKWISE or WindingOrder.COUNTER_CLOCKWISE
 */
export const calculateWindingOrder = (wallModels: IWall[]): EWindingOrder => {
  // Create an array of points, including both start and end points of each wall
  const points: THREE.Vector3[] = [];
  wallModels.forEach((wall, index) => {
    points.push(new THREE.Vector3(wall.startPoint.x, wall.startPoint.y, 0));
    // Avoid adding the same point twice if it's an end point of the current wall
    // And a start point of the next wall
    if (index === wallModels.length - 1 || wall.endPoint.x !== wallModels[index + 1].startPoint.x || wall.endPoint.y !== wallModels[index + 1].startPoint.y) {
      points.push(new THREE.Vector3(wall.endPoint.x, wall.endPoint.y, 0));
    }
  });

  let sum = 0;
  // Loop through each pair of points
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length; // Ensures the loop is closed by connecting back to the first point
    sum += (points[j].x - points[i].x) * (points[j].y + points[i].y);
  }

  // Determine the winding order based on the sign of the sum
  return sum > 0 ? EWindingOrder.CLOCKWISE : EWindingOrder.COUNTERCLOCKWISE;
};

/**
 * Check if two vectors are parallel within a specified angle tolerance.
 * @param {THREE.Vector2} v1 - The first vector.
 * @param {THREE.Vector2} v2 - The second vector.
 * @param {number} maxAngle - The maximum angle in degrees to be considered parallel.
 * @returns {boolean} - True if vectors are parallel within the specified angle, otherwise false.
 */
export const isParallel = (v1: THREE.Vector2, v2: THREE.Vector2, maxAngle: number = 1): boolean => {
  const dotProduct = v1.clone().normalize().dot(v2.clone().normalize());
  const maxCosine = Math.cos((maxAngle * Math.PI) / HALF_CIRCLE_DEGREES);
  return Math.abs(dotProduct) >= maxCosine;
};

/**
 * Calculate the unit perpendicular vector to a given vector.
 * @param {THREE.Vector2} vector - The input vector.
 * @returns {THREE.Vector2} - The unit perpendicular vector.
 */
export const calculateUnitPerpendicular = (vector: THREE.Vector2): THREE.Vector2 => new THREE.Vector2(-vector.y, vector.x).normalize();

/**
 * Function to calculate the position for the wall direction change overlay for the standard walls.
 *
 * @param {THREE.Vector3} a startPoint of the wall.
 * @param {THREE.Vector3} b endPoint of the wall.
 * @param {number} thickness thickness of the wall.
 * @returns {THREE.Vector3} position for the wall change overlay.
 */
export const getWallOverlayPosition = (a: THREE.Vector3, b: THREE.Vector3, thickness: number): THREE.Vector3 => {
  const vectorAB = new THREE.Vector3().subVectors(b, a);
  const perp = new THREE.Vector3(vectorAB.y, -vectorAB.x, vectorAB.z);
  return perp.clone().multiplyScalar(-(DEFAULT_WALL_OVERLAY_DISTANCE * thickness));
};

/**
 * Calculate intersection between two line segments defined by points a1-a2 and b1-b2.
 * @param {THREE.Vector2} a1 - First point of the first line segment.
 * @param {THREE.Vector2} a2 - Second point of the first line segment.
 * @param {THREE.Vector2} b1 - First point of the second line segment.
 * @param {THREE.Vector2} b2 - Second point of the second line segment.
 * @returns {THREE.Vector2} - Intersection point.
 */
export const calculateIntersection = (a1: THREE.Vector2, a2: THREE.Vector2, b1: THREE.Vector2, b2: THREE.Vector2): THREE.Vector2 => {
  const denominator = (a1.x - a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x - b2.x);
  return new THREE.Vector2(
    ((a1.x * a2.y - a1.y * a2.x) * (b1.x - b2.x) - (a1.x - a2.x) * (b1.x * b2.y - b1.y * b2.x)) / denominator,
    ((a1.x * a2.y - a1.y * a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x * b2.y - b1.y * b2.x)) / denominator,
  );
};

/**
 * Calculate the points forming the counter-clockwise shape.
 * @param {Array} wallModels - An array of WallModel objects representing the walls.
 * @returns {Array} - An array of points that form the counter-clockwise shape.
 */
export const calculateCounterClockwiseShapePoints = (wallModels: IWall[]): THREE.Vector2[] => {
  const points = [];

  for (let i = 0; i < wallModels.length; i++) {
    const wall = wallModels[i];
    const pointA = new THREE.Vector2(wall.startPoint.x, wall.startPoint.y);
    const pointB = new THREE.Vector2(wall.endPoint.x, wall.endPoint.y);
    const vectorAB = new THREE.Vector2().subVectors(pointB, pointA);

    // Calculate the perpendicular vector to vectorAB
    const unitPerpendicular = calculateUnitPerpendicular(vectorAB).multiplyScalar(wall.depth);

    // Calculate the two points forming the thickness of the wall
    const pointC = pointA.clone().add(unitPerpendicular);
    const pointD = pointB.clone().add(unitPerpendicular);

    // Next index with wrap-around
    const nextIndex = (i + 1) % wallModels.length;

    // Calculate next point's normal
    const nextWall = wallModels[nextIndex];
    const nextPointA = new THREE.Vector2(nextWall.startPoint.x, nextWall.startPoint.y);
    const nextPointB = new THREE.Vector2(nextWall.endPoint.x, nextWall.endPoint.y);
    const vectorNextAB = new THREE.Vector2().subVectors(nextPointB, nextPointA);
    const unitPerpendicularNext = calculateUnitPerpendicular(vectorNextAB).multiplyScalar(nextWall.depth);

    const nextOuterLinePoint1 = nextPointA.clone().add(unitPerpendicularNext);
    const nextOuterLinePoint2 = nextPointB.clone().add(unitPerpendicularNext);

    // Calculate intersection for inner point (similar to pointD in wall calculation)
    if (!isParallel(unitPerpendicular, unitPerpendicularNext)) {
      const nextIntersection = calculateIntersection(nextOuterLinePoint1, nextOuterLinePoint2, pointC, pointD);
      points.push(nextIntersection);
    }
  }

  return points;
};

/**
 * Result schema for calculating the floor shape, including indicators of whether the shape is valid.
 */
export interface IFloorShapeCalculationResult {
  shape: THREE.Shape;
  isValid: boolean;
  isCompleted: boolean;
}

/**
 * Calculates the floor shape based on walls and completion status.
 * @param {Array} wallModels - An array of WallModel objects representing the walls.
 * @param {boolean} isRoomCompleted - Optional. A flag indicating whether the room is completed.
 * @returns {THREE.Shape} - The calculated shape of the floor.
 */
export const calculateFloorShape = (
  wallModels: IWall[],
  isRoomCompleted?: boolean,
  /** When true, walls[].start/end already represent the floor boundary (single-room legacy). */
  wallsAlreadyInset: boolean = true,
): IFloorShapeCalculationResult => {
  const shape = new THREE.Shape();

  if (wallModels.length < 3 || (isRoomCompleted !== undefined && !isRoomCompleted)) {
    return { shape, isValid: true, isCompleted: false };
  }

  if (wallsAlreadyInset) {
    // Just stitch the (already-inset) start points in order
    const first = wallModels[0].startPoint;
    shape.moveTo(first.x, first.y);
    for (let i = 1; i < wallModels.length; i++) {
      const p = wallModels[i].startPoint;
      shape.lineTo(p.x, p.y);
    }
    shape.lineTo(first.x, first.y);
  } else {
    // Build an inset from the *centerline* walls (multi-room path)
    const winding = calculateWindingOrder(wallModels);
    const points = calculateInsetShapePoints(wallModels, winding); // from the fix we added earlier
    if (points.length === 0) return { shape, isValid: false, isCompleted: true };

    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) shape.lineTo(points[i].x, points[i].y);
    shape.lineTo(points[0].x, points[0].y);
  }

  const isValid = !hasIntersections(shape);
  return { shape, isValid, isCompleted: true };
};

// --- add near the top of vectorUtils.ts (helpers) ---------------------------
const EPS = 1e-3;

const v2 = (p: THREE.Vector3) => new THREE.Vector2(p.x, p.y);

const almostEqual = (a: THREE.Vector3, b: THREE.Vector3, eps = EPS) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

type EndKind = 'start' | 'end';

function whichEnd(w: IWall, p: THREE.Vector3): EndKind | null {
  if (almostEqual(w.startPoint, p)) return 'start';
  if (almostEqual(w.endPoint, p)) return 'end';
  return null;
}
const outerLineForWall = (w: IWall) => {
  const ta = new THREE.Vector2(w.startPoint.x, w.startPoint.y);
  const tb = new THREE.Vector2(w.endPoint.x, w.endPoint.y);
  const t = tb.clone().sub(ta);
  const tn = new THREE.Vector2(-t.y, t.x).normalize().multiplyScalar(w.depth);
  return { p1: ta.clone().add(tn), p2: tb.clone().add(tn) };
};

const lineIntersection = (a1: THREE.Vector2, a2: THREE.Vector2, b1: THREE.Vector2, b2: THREE.Vector2) => {
  const denom = (a1.x - a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x - b2.x);
  if (Math.abs(denom) < 1e-9) return null;
  const x = ((a1.x * a2.y - a1.y * a2.x) * (b1.x - b2.x) - (a1.x - a2.x) * (b1.x * b2.y - b1.y * b2.x)) / denom;
  const y = ((a1.x * a2.y - a1.y * a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x * b2.y - b1.y * b2.x)) / denom;
  return new THREE.Vector2(x, y);
};
const wallsAtPoint = (walls: IWall[], p: THREE.Vector3): number[] => {
  const ids: number[] = [];
  walls.forEach((w, i) => {
    if (w.type !== EWall.STANDARD) return;
    if (almostEqual(w.startPoint, p) || almostEqual(w.endPoint, p)) {
      ids.push(i);
    }
  });
  return ids;
};
/**
 * If this end has no explicit neighbor (branch in a 3/4-way), try to
 * snap its outer point to a nearby intersection with any other wall's outer line.
 */
function snapBranchEndToAnyNeighbor(
  wallModels: IWall[],
  refIndex: number,
  atPoint: THREE.Vector3,
  outerEnd: THREE.Vector2, // current C or D
  alongA: THREE.Vector2, // vectorA or vectorB
  alongB: THREE.Vector2, // other end of this wall
  maxSnapDistance: number, // e.g. 3 * wallThickness
): THREE.Vector2 {
  const incident = wallsAtPoint(wallModels, atPoint).filter((i) => i !== refIndex);
  if (incident.length === 0) return outerEnd;

  const thisA = alongA.clone();
  const thisB = alongB.clone();
  const thisDir = thisB.clone().sub(thisA).normalize();
  const thisOuterA = outerEnd.clone(); // point at the junction side

  let best: THREE.Vector2 | null = null;
  let bestDist = Infinity;

  incident.forEach((i) => {
    const w = wallModels[i];
    const { p1, p2 } = outerLineForWall(w);
    const inter = lineIntersection(thisOuterA, thisOuterA.clone().add(thisDir), p1, p2);
    if (!inter) return;

    const dist = inter.distanceTo(thisOuterA);
    if (dist < bestDist && dist <= maxSnapDistance) {
      bestDist = dist;
      best = inter;
    }
  });

  return best || outerEnd;
}

/** Smart neighbor finder:
 * - 2 walls at junction: they miter together
 * - 3 walls: the "corridor" pair (most opposite) miter; branch butts
 * - 4 walls: the most opposite pair miter; others butt
 * - otherwise: no miter
 */
export function findNeighbor(walls: IWall[], refIndex: number, atPoint: THREE.Vector3): { index: number; end: EndKind } | null {
  // Collect all incident walls + their outgoing directions
  const incident: { index: number; end: EndKind; dir: THREE.Vector2 }[] = [];

  walls.forEach((w, i) => {
    if (w.type !== EWall.STANDARD) return;
    const end = whichEnd(w, atPoint);
    if (!end) return;

    const other = end === 'start' ? w.endPoint : w.startPoint;
    const dir = v2(other).sub(v2(atPoint)).normalize();
    incident.push({ index: i, end, dir });
  });

  if (incident.length < 2) return null;

  // Helper to get "the other one" in a 2-wall junction
  const pickOtherInTwo = () => {
    const other = incident[0].index === refIndex ? incident[1] : incident[0];
    if (other.index === refIndex) return null;
    return { index: other.index, end: other.end as EndKind };
  };

  // Threshold for "roughly opposite" (cos(angle) ~ -1)
  const CORRIDOR_DOT_THRESHOLD = -0.5; // angle > ~120°

  if (incident.length === 2) {
    // Simple corner → always miter with other
    return pickOtherInTwo();
  }

  if (incident.length === 3) {
    // Find the pair whose directions are most opposite (smallest dot)
    let bestI = -1,
      bestJ = -1,
      minDot = Infinity;

    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        const dot = incident[i].dir.dot(incident[j].dir);
        if (dot < minDot) {
          minDot = dot;
          bestI = i;
          bestJ = j;
        }
      }
    }

    // If nothing reasonably opposite, skip miter in this 3-way
    if (minDot > CORRIDOR_DOT_THRESHOLD) return null;

    const corrA = incident[bestI];
    const corrB = incident[bestJ];

    // Only the corridor pair get miters between each other
    if (refIndex === corrA.index) {
      return { index: corrB.index, end: corrB.end };
    }
    if (refIndex === corrB.index) {
      return { index: corrA.index, end: corrA.end };
    }

    // Branch wall: no miter
    return null;
  }

  if (incident.length === 4) {
    // Cross junction: find the single most opposite pair
    let bestI = -1,
      bestJ = -1,
      minDot = Infinity;

    for (let i = 0; i < 4; i++) {
      for (let j = i + 1; j < 4; j++) {
        const dot = incident[i].dir.dot(incident[j].dir);
        if (dot < minDot) {
          minDot = dot;
          bestI = i;
          bestJ = j;
        }
      }
    }

    if (minDot > CORRIDOR_DOT_THRESHOLD) return null;

    const corrA = incident[bestI];
    const corrB = incident[bestJ];

    // Only this main corridor pair gets a miter
    if (refIndex === corrA.index) {
      return { index: corrB.index, end: corrB.end };
    }
    if (refIndex === corrB.index) {
      return { index: corrA.index, end: corrA.end };
    }

    // The other two arms butt
    return null;
  }

  // 5+ way junctions: keep it simple, no miter
  return null;
}

// --- NEW HELPER FUNCTIONS FOR ROBUST MITERING ---

/**
 * Gets the vector direction of a wall pointing AWAY from a specific node.
 */
const getWallDir = (wall: IWall, node: THREE.Vector3): THREE.Vector2 => {
  const isStart = almostEqual(wall.startPoint, node);
  const p1 = isStart ? wall.startPoint : wall.endPoint;
  const p2 = isStart ? wall.endPoint : wall.startPoint;
  return new THREE.Vector2(p2.x - p1.x, p2.y - p1.y).normalize();
};

/**
 * Calculates the corner point where two walls meet.
 * @param p1 Point on Wall A line
 * @param dir1 Direction of Wall A line
 * @param p2 Point on Wall B line
 * @param dir2 Direction of Wall B line
 */
const intersectLinesRaw = (p1: THREE.Vector2, dir1: THREE.Vector2, p2: THREE.Vector2, dir2: THREE.Vector2): THREE.Vector2 | null => {
  const determ = -dir1.x * dir2.y + dir1.y * dir2.x;
  if (Math.abs(determ) < 1e-9) return null; // Parallel

  const s = (-dir1.y * (p1.x - p2.x) + dir1.x * (p1.y - p2.y)) / determ;
  return new THREE.Vector2(p2.x + dir2.x * s, p2.y + dir2.y * s);
};
// --- HILFSFUNKTIONEN FÜR PERFEKTE T-JUNCTIONS ---

function distToSegmentSq(p: THREE.Vector2, v: THREE.Vector2, w: THREE.Vector2) {
  const l2 = v.distanceToSquared(w);
  if (l2 === 0) return p.distanceToSquared(v);
  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  const proj = new THREE.Vector2(v.x + t * (w.x - v.x), v.y + t * (w.y - v.y));
  return p.distanceToSquared(proj);
}

export function distanceToSegment(p: THREE.Vector2, v: THREE.Vector2, w: THREE.Vector2) {
  return Math.sqrt(distToSegmentSq(p, v, w));
}
// Echte unendliche Linien-Schnittpunkt-Berechnung
export function intersectLines(p1: THREE.Vector2, p2: THREE.Vector2, p3: THREE.Vector2, p4: THREE.Vector2): THREE.Vector2 | null {
  const den = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
  
  // FIX: Toleranz von 1e-9 auf 1e-5 erhöht! 
  // Verhindert, dass fast parallele Linien einen Schnittpunkt im Unendlichen (Monster-Spike) erzeugen.
  if (Math.abs(den) < 1e-5) return null; 
  
  const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / den;
  return new THREE.Vector2(p1.x + t * (p2.x - p1.x), p1.y + t * (p2.y - p1.y));
}

/**
 * Schneidet eine Wand (T-Stück) exakt an der Außenkante der blockierenden Wand ab
 * und taucht sie leicht in die Wand ein, um die Outlines zu verstecken!
 */
function trimToBlockingWall(
  ptCenter: THREE.Vector2,
  ptSideB: THREE.Vector2,
  ptSideA: THREE.Vector2,
  dirInward: THREE.Vector2, // Zeigt in die abzweigende Wand hinein
  wallModels: IWall[],
  currentIndex: number
) {
  let blockingWall: IWall | null = null;
  for (let i = 0; i < wallModels.length; i++) {
    if (i === currentIndex) continue;
    const w = wallModels[i];
    if (w.type !== EWall.STANDARD) continue;
    
    const wStart = new THREE.Vector2(w.startPoint.x, w.startPoint.y);
    const wEnd = new THREE.Vector2(w.endPoint.x, w.endPoint.y);
    
    // ---> DER MONSTER-KILLER <---
    // Wenn die potenzielle Wand parallel (oder auf der selben Linie) läuft, 
    // ist es ein flacher Übergang (Butt-Joint) und KEIN T-Stück.
    const wDir = new THREE.Vector2().subVectors(wEnd, wStart).normalize();
    if (Math.abs(wDir.dot(dirInward)) > 0.98) continue; // Parallel? Ignorieren!

    if (distanceToSegment(ptCenter, wStart, wEnd) < 0.05) {
      blockingWall = w;
      break;
    }
  }

  if (!blockingWall) return;

  const bStart = new THREE.Vector2(blockingWall.startPoint.x, blockingWall.startPoint.y);
  const bEnd = new THREE.Vector2(blockingWall.endPoint.x, blockingWall.endPoint.y);
  const bDir = new THREE.Vector2().subVectors(bEnd, bStart);
  const bNorm = calculateUnitPerpendicular(bDir);
  const bHalfDepth = blockingWall.depth / 2;

  const bSideBStart = bStart.clone().add(bNorm.clone().multiplyScalar(bHalfDepth));
  const bSideBEnd = bEnd.clone().add(bNorm.clone().multiplyScalar(bHalfDepth));
  const bSideAStart = bStart.clone().sub(bNorm.clone().multiplyScalar(bHalfDepth));
  const bSideAEnd = bEnd.clone().sub(bNorm.clone().multiplyScalar(bHalfDepth));

  const testPt = ptCenter.clone().add(dirInward.clone().multiplyScalar(0.5));
  const distB = distanceToSegment(testPt, bSideBStart, bSideBEnd);
  const distA = distanceToSegment(testPt, bSideAStart, bSideAEnd);

  const blockStart = distA < distB ? bSideAStart : bSideBStart;
  const blockEnd = distA < distB ? bSideAEnd : bSideBEnd;

  const farB = ptSideB.clone().add(dirInward.clone().multiplyScalar(100));
  const backB = ptSideB.clone().sub(dirInward.clone().multiplyScalar(100));
  const intB = intersectLines(backB, farB, blockStart, blockEnd);

  const farA = ptSideA.clone().add(dirInward.clone().multiplyScalar(100));
  const backA = ptSideA.clone().sub(dirInward.clone().multiplyScalar(100));
  const intA = intersectLines(backA, farA, blockStart, blockEnd);

  // DIE MAGIE: Wir stutzen die Wand nicht exakt bündig, 
  // sondern drücken sie 4cm in die Flurwand hinein (Overlap)!
  const overlap = 0.04; 

  if (intB) ptSideB.copy(intB).add(dirInward.clone().multiplyScalar(-overlap));
  if (intA) ptSideA.copy(intA).add(dirInward.clone().multiplyScalar(-overlap));
}


/**
 * Calculates pointC and pointD (Side B) and pointC2 and pointD2 (Side A) 
 * based on the Center-Line and the adjacent walls.
 */
export const calculateWallShape = (
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  wallThickness: number,
  wallModels: IWall[],
  index: number
) => {
  const vectorA = new THREE.Vector2(startPoint.x, startPoint.y);
  const vectorB = new THREE.Vector2(endPoint.x, endPoint.y);
  const vectorAB = new THREE.Vector2().subVectors(vectorB, vectorA);
  
  const unitPerpendicular = calculateUnitPerpendicular(vectorAB);
  const halfDepth = wallThickness / 2;
  const normalOffset = unitPerpendicular.clone().multiplyScalar(halfDepth);

  const pointC = vectorA.clone().add(normalOffset);
  const pointD = vectorB.clone().add(normalOffset);
  const pointC2 = vectorA.clone().sub(normalOffset);
  const pointD2 = vectorB.clone().sub(normalOffset);

  const cutoffOffset = 5;

  const startNeighbor = findNeighbor(wallModels, index, startPoint);
  const endNeighbor = findNeighbor(wallModels, index, endPoint);

  const getNeighborLines = (neighborInfo: { index: number; end: 'start' | 'end' }) => {
    const nWall = wallModels[neighborInfo.index];
    const nHalfDepth = nWall.depth / 2;
    const nA = new THREE.Vector2(nWall.startPoint.x, nWall.startPoint.y);
    const nB = new THREE.Vector2(nWall.endPoint.x, nWall.endPoint.y);
    
    const nAB = new THREE.Vector2().subVectors(nB, nA);
    const nNormal = calculateUnitPerpendicular(nAB);
    const nOffset = nNormal.clone().multiplyScalar(nHalfDepth);

    return {
      nNormal,
      nDepth: nWall.depth,
      nSideBStart: nA.clone().add(nOffset),
      nSideBEnd: nB.clone().add(nOffset),
      nSideAStart: nA.clone().sub(nOffset),
      nSideAEnd: nB.clone().sub(nOffset),
    };
  };

  // --- START PUNKT ---
  if (startNeighbor) {
    const { nNormal, nDepth, nSideBStart, nSideBEnd, nSideAStart, nSideAEnd } = getNeighborLines(startNeighbor);
    if (!isParallel(unitPerpendicular, nNormal)) {
      const interB = calculateIntersection(pointC, pointD, nSideBStart, nSideBEnd);
      const interA = calculateIntersection(pointC2, pointD2, nSideAStart, nSideAEnd);
      const maxSpikeDistance = cutoffOffset * Math.min(wallThickness, nDepth);

      if (interB && interA && interB.distanceTo(vectorA) < maxSpikeDistance && interA.distanceTo(vectorA) < maxSpikeDistance) {
        pointC.set(interB.x, interB.y);
        pointC2.set(interA.x, interA.y);
      }
    }
  } else {
    // KEIN MITER (T-Junction Branch oder freies Ende) -> Bündig abschneiden!
    const dirInward = vectorAB.clone().normalize();
    trimToBlockingWall(vectorA, pointC, pointC2, dirInward, wallModels, index);
  }

  // --- END PUNKT ---
  if (endNeighbor) {
    const { nNormal, nDepth, nSideBStart, nSideBEnd, nSideAStart, nSideAEnd } = getNeighborLines(endNeighbor);
    if (!isParallel(unitPerpendicular, nNormal)) {
      const interB = calculateIntersection(pointC, pointD, nSideBStart, nSideBEnd);
      const interA = calculateIntersection(pointC2, pointD2, nSideAStart, nSideAEnd);
      const maxSpikeDistance = cutoffOffset * Math.min(wallThickness, nDepth);

      if (interB && interA && interB.distanceTo(vectorB) < maxSpikeDistance && interA.distanceTo(vectorB) < maxSpikeDistance) {
        pointD.set(interB.x, interB.y);
        pointD2.set(interA.x, interA.y);
      }
    }
  } else {
    // KEIN MITER (T-Junction Branch oder freies Ende) -> Bündig abschneiden!
    const dirInward = vectorAB.clone().normalize().negate();
    trimToBlockingWall(vectorB, pointD, pointD2, dirInward, wallModels, index);
  }

  return {
    pointC: new THREE.Vector3(pointC.x, pointC.y, 0),
    pointD: new THREE.Vector3(pointD.x, pointD.y, 0),
    pointC2: new THREE.Vector3(pointC2.x, pointC2.y, 0),
    pointD2: new THREE.Vector3(pointD2.x, pointD2.y, 0),
  };
};

export const createWallShape = (
  startPoint: THREE.Vector3,
  endPoint: THREE.Vector3,
  outerStartPoint: THREE.Vector3,
  outerEndPoint: THREE.Vector3,
  outerStartPoint2: THREE.Vector3,
  outerEndPoint2: THREE.Vector3,
): THREE.Shape => {
  const outerStartVec = new THREE.Vector2(outerStartPoint.x, outerStartPoint.y);
  const outerEndVec = new THREE.Vector2(outerEndPoint.x, outerEndPoint.y);
  const outerStartVec2 = new THREE.Vector2(outerStartPoint2.x, outerStartPoint2.y);
  const outerEndVec2 = new THREE.Vector2(outerEndPoint2.x, outerEndPoint2.y);

  const wallShape = new THREE.Shape();
  wallShape.moveTo(outerStartVec.x, -outerStartVec.y);
  wallShape.lineTo(outerEndVec.x, -outerEndVec.y);
  wallShape.lineTo(outerEndVec2.x, -outerEndVec2.y);
  wallShape.lineTo(outerStartVec2.x, -outerStartVec2.y);
  wallShape.closePath();

  return wallShape;
};
export function getAngleFromPoints(start: THREE.Vector3, end: THREE.Vector3): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.atan2(dy, dx);
}
/**
 * Calculate the inset (inner) polygon for a closed loop of walls.
 * Works for both CW and CCW windings by choosing the correct inward normal.
 */
export const calculateInsetShapePoints = (wallModels: IWall[], winding: EWindingOrder): THREE.Vector2[] => {
  if (!wallModels || wallModels.length < 3) return [];

  const inwardNormal = (ab: THREE.Vector2, depth: number) => {
    // inward depends on winding
    const n =
      winding === EWindingOrder.CLOCKWISE
        ? new THREE.Vector2(ab.y, -ab.x) // right normal
        : new THREE.Vector2(-ab.y, ab.x); // left normal
    return n.normalize().multiplyScalar(depth);
  };

  const lines: Array<{ p1: THREE.Vector2; p2: THREE.Vector2 }> = [];
  for (let i = 0; i < wallModels.length; i++) {
    const w = wallModels[i];
    const A = new THREE.Vector2(w.startPoint.x, w.startPoint.y);
    const B = new THREE.Vector2(w.endPoint.x, w.endPoint.y);
    const AB = B.clone().sub(A);

    const nIn = inwardNormal(AB, w.depth);
    // Inset line for this segment
    lines.push({ p1: A.clone().add(nIn), p2: B.clone().add(nIn) });
  }

  const isParallel2 = (v1: THREE.Vector2, v2: THREE.Vector2, maxAngleDeg = 1) => {
    const dot = v1.clone().normalize().dot(v2.clone().normalize());
    const maxCos = Math.cos((maxAngleDeg * Math.PI) / 180);
    return Math.abs(dot) >= maxCos;
  };

  const intersect = (a1: THREE.Vector2, a2: THREE.Vector2, b1: THREE.Vector2, b2: THREE.Vector2) => {
    const denom = (a1.x - a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x - b2.x);
    // denom ~ 0 → parallel or nearly
    if (Math.abs(denom) < 1e-9) return null;
    const x = ((a1.x * a2.y - a1.y * a2.x) * (b1.x - b2.x) - (a1.x - a2.x) * (b1.x * b2.y - b1.y * b2.x)) / denom;
    const y = ((a1.x * a2.y - a1.y * a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x * b2.y - b1.y * b2.x)) / denom;
    return new THREE.Vector2(x, y);
  };

  const pts: THREE.Vector2[] = [];
  const n = lines.length;
  for (let i = 0; i < n; i++) {
    const curr = lines[i];
    const next = lines[(i + 1) % n];

    const currDir = curr.p2.clone().sub(curr.p1);
    const nextDir = next.p2.clone().sub(next.p1);

    let P = intersect(curr.p1, curr.p2, next.p1, next.p2);

    // Fallbacks for parallel/near-parallel
    if (!P || isParallel2(currDir, nextDir)) {
      // Use the “corner” point from the original, inset by the current wall
      // This keeps the loop closed even with collinear walls.
      P = next.p1.clone();
    }

    pts.push(P);
  }

  return pts;
}; 
export const reorderWindingOrderToClockwise = (walls: IStandardWall[]): IStandardWall[] => {
  const byUuid = new Map(walls.map((w) => [w.uuid, w] as const));

  return [...walls].reverse().map((wall) => {
    const orig = byUuid.get(wall.uuid);
    if (!orig) return wall;

    const startPoint = orig.startPoint.clone();
    orig.startPoint = orig.endPoint.clone();
    orig.endPoint = startPoint;

    if (typeof (orig as any).updateWallLengths === 'function') {
      (orig as any).updateWallLengths();
    }

    return orig;
  });
};
export function calculatePolygonCentroid(points: THREE.Vector2[]): THREE.Vector2 {
  const centroid = new THREE.Vector2(0, 0);
  let signedArea = 0;
  for (let i = 0; i < points.length; i++) {
    const x0 = points[i].x;
    const y0 = points[i].y;
    const x1 = points[(i + 1) % points.length].x;
    const y1 = points[(i + 1) % points.length].y;
    const a = x0 * y1 - x1 * y0;
    signedArea += a;
    centroid.x += (x0 + x1) * a;
    centroid.y += (y0 + y1) * a;
  }
  signedArea /= 2;
  centroid.x /= POLYGON_CENTROID_MULTIPLIER * signedArea;
  centroid.y /= POLYGON_CENTROID_MULTIPLIER * signedArea;
  return centroid;
}

export function calculatePolygonArea(vertices: THREE.Vector2[]): number {
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  return Math.abs(area) / 2;
}

export function calculatePerimeter(walls: IWall[]): number {
  let perimeter = 0;
  walls.forEach((wall) => {
    const x1 = wall.startPoint.x,
      y1 = wall.startPoint.y;
    const x2 = wall.endPoint.x,
      y2 = wall.endPoint.y;
    const distance = Math.hypot(x2 - x1, y2 - y1);
    perimeter += distance;
  });
  return perimeter;
}

export const areArraysDeepEqual = (arr1: [], arr2: []): boolean => {
  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) return false;
  }
  return true;
};

// Utility function to calculate wall length
export const calculateWallLength = (startPoint: THREE.Vector3, endPoint: THREE.Vector3): number => {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  return Math.sqrt(dx * dx + dy * dy);
};
const close = (a: Vector3, b: Vector3, eps: number) => a.distanceTo(b) <= eps;

function tryBuildClosedChain(walls: IStandardWall[], eps: number, startFlip: boolean): boolean {
  if (walls.length < 3) return false;

  const remaining = new Set<IStandardWall>(walls);
  const first = walls[0];
  remaining.delete(first);

  // pick orientation for the first wall
  const startPoint = (startFlip ? first.endPoint : first.startPoint).clone();
  let cursor = (startFlip ? first.startPoint : first.endPoint).clone();

  while (remaining.size > 0) {
    let nextWall: IStandardWall | null = null;
    let nextCursor: Vector3 | null = null;

    for (const w of remaining) {
      if (close(w.startPoint, cursor, eps)) {
        nextWall = w;
        nextCursor = w.endPoint;
        break;
      }
      if (close(w.endPoint, cursor, eps)) {
        nextWall = w;
        nextCursor = w.startPoint; // flipped
        break;
      }
    }

    if (!nextWall || !nextCursor) return false;

    remaining.delete(nextWall);
    cursor = nextCursor.clone();
  }

  // finally, last point must return to the chain start
  return close(cursor, startPoint, eps);
}

// Helper to create a unique string key for coordinates to handle float precision
const COORD_EPSILON = 1e-4; // 0.1mm tolerance
const getPointKey = (v: Vector3): string => {
  // Round to precision to merge close points
  const x = Math.round(v.x / COORD_EPSILON);
  const y = Math.round(v.y / COORD_EPSILON);
  return `${x}_${y}`;
};

/**
 * Validates if the provided walls form at least one closed room.
 * * @param walls All walls in the building
 * @param strict If true, returns true ONLY if *every* wall is part of a closed room (no dangling walls).
 * If false, returns true if *at least one* closed room exists.
 */

// Tolerance matching your HEAL_TOLERANCE (e.g. 1mm precision for keys)
const KEY_PRECISION = 1000;

/**
 * Generates a spatial hash key that ignores microscopic float errors.
 * (10.0001, 20.0000) -> "10000,20000"
 */
function getRobustPointKey(v: THREE.Vector3): string {
  // Round to nearest millimeter (or whatever precision you prefer)
  const x = Math.round(v.x * KEY_PRECISION);
  const y = Math.round(v.y * KEY_PRECISION);
  return `${x},${y}`;
}

/**
 * Validates if the walls form a closed geometric loop using Graph Theory.
 * Uses robust keys to ignore floating point drift.
 * * @param walls List of walls to check
 * @param strict If true, requires ALL walls to be part of a loop (no dead ends allowed).
 */
export const validateClosedRooms = (walls: IWall[], strict: boolean = false): boolean => {
  const standardWalls = walls.filter((w) => w && w.type === EWall.STANDARD) as StandardWallModel[];

  // A single triangle is the smallest possible room (3 walls)
  if (standardWalls.length < 3) return false;

  // 1. Build Adjacency Graph (Node -> Set of Wall IDs)
  const adjacency = new Map<string, Set<string>>();
  const wallEndpoints = new Map<string, { start: string; end: string }>();

  standardWalls.forEach((w) => {
    const k1 = getRobustPointKey(w.startPoint);
    const k2 = getRobustPointKey(w.endPoint);

    // Ignore zero-length geometry skips
    if (k1 === k2) return;

    if (!adjacency.has(k1)) adjacency.set(k1, new Set());
    if (!adjacency.has(k2)) adjacency.set(k2, new Set());

    adjacency.get(k1)!.add(w.uuid);
    adjacency.get(k2)!.add(w.uuid);

    wallEndpoints.set(w.uuid, { start: k1, end: k2 });
  });

  // 2. Iteratively Prune Dead Ends (Degree <= 1)
  // We remove nodes that don't continue a path. If a loop exists, its nodes have degree >= 2.
  const activeWallUuids = new Set(standardWalls.map((w) => w.uuid));
  const activeNodes = new Set(adjacency.keys());
  let changed = true;

  while (changed) {
    changed = false;
    const nodesToRemove: string[] = [];

    for (const nodeKey of activeNodes) {
      const wallIds = adjacency.get(nodeKey);
      if (!wallIds) continue;

      // Count how many *active* walls connect to this node
      let degree = 0;
      wallIds.forEach((uuid) => {
        if (activeWallUuids.has(uuid)) degree++;
      });

      // If it's a dead end (1 wall) or orphan (0 walls), mark for removal
      if (degree <= 1) {
        nodesToRemove.push(nodeKey);
      }
    }

    if (nodesToRemove.length > 0) {
      changed = true;
      nodesToRemove.forEach((nodeKey) => {
        activeNodes.delete(nodeKey);

        // "Delete" walls attached to this dead node from the active set
        const wallsAtNode = adjacency.get(nodeKey);
        wallsAtNode?.forEach((uuid) => activeWallUuids.delete(uuid));
      });
    }
  }

  // 3. Final Check
  if (activeWallUuids.size === 0) return false; // No loops found

  if (strict) {
    // Strict: Every single input wall must be part of the loop.
    // If activeWalls < inputWalls, it means we pruned some "dangling" walls.
    return activeWallUuids.size === standardWalls.length;
  }

  // Loose: At least one loop exists (size >= 3)
  return activeWallUuids.size >= 3;
};
export const isRoomClosed = (walls: IWall[], epsilon: number = ROOM_COMPLETION_TOLERANCE): boolean => {
  const standardWalls = walls.filter((w) => w.type === EWall.STANDARD) as IStandardWall[];
  if (standardWalls.length < 3) return false;

  // try both orientations for the first wall
  return tryBuildClosedChain(standardWalls, epsilon, false) || tryBuildClosedChain(standardWalls, epsilon, true);
};

const isSame = (a: THREE.Vector3, b: THREE.Vector3) => a.distanceTo(b) < EPS;

export const adjustWallLength = (startPoint: THREE.Vector3, endPoint: THREE.Vector3, newLength: number, changeDirection?: EDirection): THREE.Vector3[] => {
  const startVec = new THREE.Vector3(startPoint.x, startPoint.y, 0);
  const endVec = new THREE.Vector3(endPoint.x, endPoint.y, 0);

  const direction = endVec.clone().sub(startVec).normalize();
  const currentLength = startVec.distanceTo(endVec);
  const lengthChange = newLength - currentLength;

  let newStartPoint = startVec;
  let newEndPoint = endVec;

  if (changeDirection !== undefined) {
    if (changeDirection === EDirection.BOTH) {
      newStartPoint = startVec.clone().add(direction.clone().multiplyScalar(-lengthChange / 2));
      newEndPoint = endVec.clone().add(direction.clone().multiplyScalar(lengthChange / 2));
    } else if (changeDirection === EDirection.LEFT) {
      newStartPoint = startVec.clone().add(direction.clone().multiplyScalar(-lengthChange));
    } else {
      newEndPoint = endVec.clone().add(direction.clone().multiplyScalar(lengthChange));
    }
  } else {
    newStartPoint = startVec.clone().add(direction.clone().multiplyScalar(-lengthChange / 2));
    newEndPoint = endVec.clone().add(direction.clone().multiplyScalar(lengthChange / 2));
  }

  return [newStartPoint, newEndPoint];
};

export const updateConnectedWallLengths = (allWalls: IStandardWall[], targetIndex: number, newLength: number, direction: EDirection): IStandardWall[] => {
  // 1. Clone all walls to avoid mutation
  const updatedWalls = allWalls.map((w) => {
    const nw = new StandardWallModel(w.startPoint.clone(), w.endPoint.clone(), w.depth, w.windows, w.doors, [], w.attributes, w.uuid, w.passages);
    // Copy sides manually if needed, or rely on constructor if it handles them
    nw.sides = w.sides; // Shallow copy of sides is okay if we don't mutate internal arrays deeply here
    return nw;
  });

  const targetWall = updatedWalls[targetIndex];
  const oldStart = targetWall.startPoint.clone();
  const oldEnd = targetWall.endPoint.clone();

  // 2. Calculate new positions
  const [newStart, newEnd] = adjustWallLength(oldStart, oldEnd, newLength, direction);

  // 3. Apply to target
  targetWall.startPoint.copy(newStart);
  targetWall.endPoint.copy(newEnd);

  // 4. Update Neighbors (Graph-based)
  updatedWalls.forEach((w, idx) => {
    if (idx === targetIndex) return;

    // Check Start Junction
    if (isSame(w.startPoint, oldStart)) w.startPoint.copy(newStart);
    else if (isSame(w.endPoint, oldStart)) w.endPoint.copy(newStart);

    // Check End Junction
    if (isSame(w.startPoint, oldEnd)) w.startPoint.copy(newEnd);
    else if (isSame(w.endPoint, oldEnd)) w.endPoint.copy(newEnd);
  });

  return updatedWalls;
};

export function distanceToWallNormal(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
  const ab = b.clone().sub(a);
  const normal = new THREE.Vector3(-ab.y, ab.x, 0).normalize(); // wall normal
  const ap = p.clone().sub(a);
  return Math.abs(ap.dot(normal));
}



/**
 * Calculates the closest point on a line segment to a given point.
 * This function helps to determine the shortest path from a point to a segment.
 *
 * @param {THREE.Vector2} p - The point from which the closest point on the segment is sought.
 * @param {THREE.Vector2} a - The start point of the segment.
 * @param {THREE.Vector2} b - The end point of the segment.
 * @returns {THREE.Vector2} - The closest point on the segment to point p.
 */
const closestPointOnSegment = (p: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2) => {
  const ab = b.clone().sub(a);
  const t = Math.max(0, Math.min(1, p.clone().sub(a).dot(ab) / ab.dot(ab)));
  return a.clone().add(ab.multiplyScalar(t));
};

/**
 * Checks if a point is within a polygon with an optional tolerance.
 * It first checks if the point is strictly inside the polygon.
 * If not inside, it checks if the point is within the specified tolerance of the polygon's edge.
 *
 * @param {THREE.Vector2} point - The point to check.
 * @param {THREE.Vector2[]} polygon - The vertices of the polygon.
 * @param {number} [tolerance=0] - The distance tolerance for considering the point as inside.
 * @returns {boolean} - True if the point is inside the polygon or within the tolerance of its edge.
 */
export const isPointInPolygon = (point: THREE.Vector2, polygon: THREE.Vector2[], tolerance: number = 0) => {
  // Check if the point is inside the polygon without any tolerance
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  // If the point is inside, return true immediately
  if (inside) return true;

  // If not inside, check the distance to the closest edge to apply tolerance
  let minDist = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const start = new THREE.Vector2(polygon[j].x, polygon[j].y);
    const end = new THREE.Vector2(polygon[i].x, polygon[i].y);
    const distance = distanceToSegment(point, start, end);
    minDist = Math.min(minDist, distance);
  }

  // Check if the closest distance is within the tolerance
  return minDist <= tolerance;
};

/**
 * Snaps a 3D point to the nearest point on a polygon if it's outside, respecting a specified tolerance.
 * If the point is already within the polygon or the tolerance, it remains unchanged.
 *
 * @param {THREE.Vector3} point - The 3D point to snap to the polygon.
 * @param {THREE.Vector2[]} polygon - The vertices of the polygon.
 * @param {number} [tolerance=POLYGON_BOUNDARY_TOLERANCE] - The distance tolerance for snapping.
 * @returns {THREE.Vector3} - The snapped or original point.
 */
const snapPointToPolygon = (point: THREE.Vector3, polygon: THREE.Vector2[], tolerance: number = POLYGON_BOUNDARY_TOLERANCE) => {
  const vec2Point = new THREE.Vector2(point.x, point.y);
  if (isPointInPolygon(vec2Point, polygon, tolerance)) {
    return point; // If the point is inside the polygon or its tolerance then no snapping needed
  }

  // Find the closest point on the polygon's edges
  let minDist = Infinity;
  let closestPoint = vec2Point.clone();
  for (let i = 0; i < polygon.length; i++) {
    const start = polygon[i];
    const end = polygon[(i + 1) % polygon.length];
    const closest = closestPointOnSegment(vec2Point, start, end);
    const dist = vec2Point.distanceTo(closest);
    if (dist < minDist) {
      minDist = dist;
      closestPoint = closest;
    }
  }
  return new THREE.Vector3(closestPoint.x, closestPoint.y, point.z);
};

/**
 * Adjusts the start and end points of a wall model based on its proximity to a specified floor shape.
 * It snaps the wall points to the floor polygon if necessary and calculates the displacements required
 * to maintain the original wall length.
 *
 * @param {IPartitionWall} wallModel - The wall model to adjust.
 * @param {THREE.Shape} floorShape - The floor shape to use for snapping.
 * @returns {Object} - The necessary displacements and the new snapped positions of the wall endpoints.
 */
export const getSnappedWallPoints = (wallModel: IPartitionWall, floorShape: THREE.Shape) => {
  const points = floorShape.extractPoints(12).shape;
  const polygon = points.map((p) => new THREE.Vector2(p.x, p.y));

  const start = wallModel.startPoint.clone();
  const end = wallModel.endPoint.clone();

  const snappedStart = snapPointToPolygon(start, polygon, POLYGON_BOUNDARY_TOLERANCE);
  const snappedEnd = snapPointToPolygon(end, polygon, POLYGON_BOUNDARY_TOLERANCE);

  // Calculate the necessary displacement to adjust both points while maintaining length
  const displacementStart = new THREE.Vector3().subVectors(snappedStart, start);
  const displacementEnd = new THREE.Vector3().subVectors(snappedEnd, end);

  return { displacementStart, displacementEnd, snappedStart, snappedEnd };
};

/**
 * Updates a wall model by checking its boundaries against a set of standard walls and applying any necessary corrections.
 * It uses a calculated floor shape to determine if the wall endpoints need adjustment and applies the necessary displacement.
 *
 * @param {IPartitionWall} wallModel - The wall model to update.
 * @param {IWall[]} standardWalls - A collection of walls to calculate the floor shape.
 * @param {Function} onWallUpdate - Callback to execute after updating the wall.
 * @param {number} index - Index of the wall being updated.
 */
export function updateWallWithBoundaryCheck(wallModel: IPartitionWall, standardWalls: IWall[], onWallUpdate: (wallUuid: string, wallModel: IWall) => void, index: number): void {
  const floorShape = calculateFloorShape(standardWalls).shape;
  const { displacementStart, displacementEnd } = getSnappedWallPoints(wallModel, floorShape);

  // Apply corrections based on boundary checks
  const displacement = new THREE.Vector3(
    (displacementStart.x + displacementEnd.x) / 2,
    (displacementStart.y + displacementEnd.y) / 2,
    (displacementStart.z + displacementEnd.z) / 2,
  );

  if (displacement.lengthSq() > 0) {
    wallModel.startPoint.add(displacement);
    wallModel.endPoint.add(displacement);
    onWallUpdate(index, wallModel);
  }
}

/**
 * Interpolates points along a line segment.
 * @param {THREE.Vector2} start - The start point of the line segment.
 * @param {THREE.Vector2} end - The end point of the line segment.
 * @param {number} numPoints - Number of points to interpolate along the line.
 * @returns {THREE.Vector2[]} - An array of interpolated points.
 */
const interpolatePoints = (start: THREE.Vector2, end: THREE.Vector2, numPoints: number): THREE.Vector2[] => {
  const points: THREE.Vector2[] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const x = start.x + t * (end.x - start.x);
    const y = start.y + t * (end.y - start.y);
    points.push(new THREE.Vector2(x, y));
  }
  return points;
};

/**
 * Check if two Vector3 points are equal.
 * @param {THREE.Vector3} point1 - The first point.
 * @param {THREE.Vector3} point2 - The second point.
 * @returns {boolean} - True if the points are identical, otherwise false.
 */
export const pointsAreEqual = (point1: THREE.Vector3, point2: THREE.Vector3): boolean => point1.x === point2.x && point1.y === point2.y && point1.z === point2.z;

/**
 * Computes the percentage of the line segment that is within a polygon.
 * @param {THREE.Vector2} startPoint - The start point of the line segment.
 * @param {THREE.Vector2} endPoint - The end point of the line segment.
 * @param {THREE.Shape} polygonShape - The shape of the polygon.
 * @param {number} [tolerance=0] - The distance tolerance for considering points as inside.
 * @param {number} [numPoints=10] - Number of points to interpolate along the line segment.
 * @returns {number} - The percentage of the line segment within the polygon.
 */
export const percentageWallInPolygon = (startPoint: THREE.Vector2, endPoint: THREE.Vector2, polygonShape: THREE.Shape, tolerance: number = 0, numPoints: number = 10): number => {
  const polygon = polygonShape.extractPoints(20).shape;
  const points = interpolatePoints(startPoint, endPoint, numPoints);
  let insideCount = 0;

  for (const point of points) {
    if (isPointInPolygon(point, polygon, tolerance)) {
      insideCount++;
    }
  }
  return (insideCount / points.length) * 100;
};

/**
 * Function to calculate the position for wall direction change overlay.
 *
 * @param wall the standard wall that needs its parallel mid point calculated.
 * @param {number} distance optional distance that the parallel point should lie from the wall.
 * @returns {THREE.Vector3} point that lies parallel to the wall on the perpendicular mid line.
 */
export const calculateWallParallelMidPoint = (wall: StandardWallModel, distance: number): THREE.Vector3 => {
  const dir = new THREE.Vector3().subVectors(wall.outerEndPoint, wall.outerStartPoint).normalize();
  const perp = new THREE.Vector3(-dir.y, dir.x, 0);
  const mid = new THREE.Vector3().addVectors(wall.startPoint, wall.endPoint).multiplyScalar(0.5);
  const result = new THREE.Vector3().addVectors(mid, perp.multiplyScalar(wall.depth).addScaledVector(perp.normalize(), distance + wall.depth));
  return result;
};

/**
 * Function to calculate the angle in degrees, that the line representing a wall has relative to the x-axis.
 *
 * @param {StandardWallModel} wall the standard wall model.
 * @returns {number} the angle in degrees.
 */
export const calculateAbsoluteWallAngle = (wall: StandardWallModel): number => {
  // Create a vector representing the wall direction.
  const wallVector = new THREE.Vector3().subVectors(wall.endPoint, wall.startPoint);

  // Calculate the angle using atan2.
  // Negative sign makes it clockwise instead of counterclockwise.
  let angle = -Math.atan2(wallVector.y, wallVector.x);

  // Convert to degrees.
  angle = (angle * 180) / Math.PI;

  // Normalize to 0-360 range.
  if (angle < 0) {
    angle += 360;
  }

  return angle;
};

/**
 * Function to calculate new start and end points for a wall rotated by 90 degrees.
 *
 * @param {THREE.Vector3} startPoint The starting point of the wall.
 * @param {THREE.Vector3} endPoint The ending point of the wall.
 * @param {boolean} [clockwise=false] Whether to rotate the wall clockwise. Defaults to counterclockwise.
 * @returns {{ newStartPoint: THREE.Vector3, newEndPoint: THREE.Vector3 }} The new start and end points after rotation.
 */
export const rotateWall90Degrees = (startPoint: THREE.Vector3, endPoint: THREE.Vector3, clockwise: boolean = false) => {
  // Calculate the direction vector from startPoint to endPoint
  const direction = new THREE.Vector3(
    endPoint.x - startPoint.x,
    endPoint.y - startPoint.y,
    0, // Assuming the wall lies in the XY plane
  );

  // Rotate the direction vector by 90 degrees
  const rotatedDirection = clockwise
    ? new THREE.Vector3(direction.y, -direction.x, 0) // Clockwise rotation
    : new THREE.Vector3(-direction.y, direction.x, 0); // Counterclockwise rotation

  // Compute new start and end points
  const newStartPoint = new THREE.Vector3(startPoint.x + rotatedDirection.x, startPoint.y + rotatedDirection.y, 0);

  const newEndPoint = new THREE.Vector3(endPoint.x + rotatedDirection.x, endPoint.y + rotatedDirection.y, 0);

  return { newStartPoint, newEndPoint };
};

/**
 * Function to move Point A along a line defined by points A and B, by a specified distance.
 *
 * @param A Point A.
 * @param B Point B.
 * @param distance The distance to move along the line.
 * @returns {THREE.Vector3} The new position of Point A.
 * Formula from: https://math.stackexchange.com/questions/3346085/extend-line-in-xy-plane-when-start-endpoint-and-direction-is-known
 */
export const movePointAAlongLine = (A: THREE.Vector3, B: THREE.Vector3, distance: number): THREE.Vector3 => {
  // Compute direction vector AB
  const direction = new THREE.Vector3(B.x - A.x, B.y - A.y, (B.z || 0) - (A.z || 0));

  // Compute Euclidean length
  const length = direction.length();

  // Normalize direction vector to get the unit vector
  const unitVector = direction.divideScalar(length);

  // Move A along the direction
  return new THREE.Vector3(A.x - unitVector.x * distance, A.y - unitVector.y * distance, (A.z || 0) - unitVector.z * distance);
};

/**
 * Function to move Point B along a line defined by points A and B, by a specified distance.
 *
 * @param A Point A.
 * @param B  Point B.
 * @param distance The distance to move along the line.
 * @returns {THREE.Vector3} The new position of Point B.
 * Fromular from: https://math.stackexchange.com/questions/3346085/extend-line-in-xy-plane-when-start-endpoint-and-direction-is-known
 */
export const movePointBAlongLine = (A: THREE.Vector3, B: THREE.Vector3, distance: number): THREE.Vector3 => {
  // Compute direction vector AB
  const direction = new THREE.Vector3(B.x - A.x, B.y - A.y, (B.z || 0) - (A.z || 0));

  // Compute Euclidean length
  const length = direction.length();

  // Normalize direction vector to get the unit vector
  const unitVector = direction.divideScalar(length);

  // Move B along the direction
  return new THREE.Vector3(B.x + unitVector.x * distance, B.y + unitVector.y * distance, (B.z || 0) + unitVector.z * distance);
};

/**
 * Function to determine if three points form a counter-clockwise angle.
 *
 * @param pointA - Coordinates of point A.
 * @param pointB - Coordinates of point B.
 * @param pointC - Coordinates of point C.
 * @returns {number} - A value indicating the orientation of the points.
 * Positive for counter-clockwise, negative for clockwise, 0 for collinear.
 * Formula: (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y)
 */
export const counterClockwiseAngle = (pointA: THREE.Vector2 | THREE.Vector3, pointB: THREE.Vector2 | THREE.Vector3, pointC: THREE.Vector2 | THREE.Vector3): number =>
  (pointB.x - pointA.x) * (pointC.y - pointA.y) - (pointC.x - pointA.x) * (pointB.y - pointA.y);

/**
 * Function to check if two line segments intersect.
 *
 * @param pointAp - Start point of the first segment.
 * @param pointAq - End point of the first segment.
 * @param pointBp - Start point of the second segment.
 * @param pointBq - End point of the second segment.
 * @param sensitivityThreshold - Optional, Threshold to adjust the precision of the intersection check.
 * @returns {boolean} - Flag indicating whether two segments are intersecting.
 */
export const areLineSegmentsIntersecting = (
  pointAp: THREE.Vector2 | THREE.Vector3,
  pointAq: THREE.Vector2 | THREE.Vector3,
  pointBp: THREE.Vector2 | THREE.Vector3,
  pointBq: THREE.Vector2 | THREE.Vector3,
  sensitivityThreshold: number = 0,
): boolean => {
  // Helper function to determine the orientation of three points
  const _orientation = (p: THREE.Vector2 | THREE.Vector3, q: THREE.Vector2 | THREE.Vector3, r: THREE.Vector2 | THREE.Vector3) => {
    const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    return Math.abs(val) < sensitivityThreshold ? 0 : val > 0 ? 1 : -1; // 0 = collinear, 1 = clockwise, -1 = counterclockwise
  };

  // Helper function to check if a point lies on a segment
  const _onSegment = (p: THREE.Vector2 | THREE.Vector3, q: THREE.Vector2 | THREE.Vector3, r: THREE.Vector2 | THREE.Vector3) =>
    Math.min(p.x, r.x) <= q.x + sensitivityThreshold &&
    q.x <= Math.max(p.x, r.x) + sensitivityThreshold &&
    Math.min(p.y, r.y) <= q.y + sensitivityThreshold &&
    q.y <= Math.max(p.y, r.y) + sensitivityThreshold;

  // Compute orientations for the four ordered triplets
  const o1 = _orientation(pointAp, pointAq, pointBp);
  const o2 = _orientation(pointAp, pointAq, pointBq);
  const o3 = _orientation(pointBp, pointBq, pointAp);
  const o4 = _orientation(pointBp, pointBq, pointAq);

  // General case: segments intersect if orientations are different
  if (o1 !== o2 && o3 !== o4) return true;

  // Special cases: check if a point lies on the other segment
  if (o1 === 0 && _onSegment(pointAp, pointBp, pointAq)) return true;
  if (o2 === 0 && _onSegment(pointAp, pointBq, pointAq)) return true;
  if (o3 === 0 && _onSegment(pointBp, pointAp, pointBq)) return true;
  if (o4 === 0 && _onSegment(pointBp, pointAq, pointBq)) return true;

  // No intersection
  return false;
};
